<template>
  <v-container v-resize="getWindowSize" class="px-5 my-5">
    <h4 class="title">Recurrent Payments</h4>
    <v-row no-gutters class="my-6">
      <v-col cols="12" md="6" class="d-flex justify-start align-center">
        <h4 class="body-1" style="color: #2661FF;">
          {{ $t("recurring_debit.subtitle") }}
        </h4>
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-end align-center">
        <v-btn
          @click="openUpload"
          class="mx-2 rounded-lg"
          color="#2661FF"
          dark
          ref="button-open-upload"
          outlined
        >
          <span>{{ $t("recurring_debit.main.upload") }}</span>
          <v-icon class="ml-1">
            upload
          </v-icon>
        </v-btn>
        <v-btn
          @click="openGenerate"
          class="mx-2 rounded-lg"
          color="#2661FF"
          ref="button-open-generate"
        >
          <span style="color: #fff">{{
            $t("recurring_debit.main.generate")
          }}</span>
          <v-icon class="ml-2" color="white">
            cloud_done
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12">
        <v-card class="custom_card rounded-lg" outlined flat>
          <v-card-title>
            <v-row dense>
              <v-col cols="2">
                <v-btn
                  class="rounded-lg"
                  outlined
                  color="#2661FF"
                  @click="getDRBFiles"
                >
                  <v-icon>
                    refresh
                  </v-icon>
                  <span style="text-transform: capitalize;">
                    {{ $t("actions.update") }}
                  </span>
                </v-btn>
              </v-col>
              <v-col cols="10" class="d-flex justify-end">
                <DateBar
                  :dateRange="dateRange"
                  @updateRange="
                    (value, startDate, index) => {
                      dateRange = index;
                      endDate = $moment().format('YYYY-MM-DD');
                      changeDates(startDate, endDate);
                    }
                  "
                  format-date="YYYY-MM-DD"
                />
                <DateFilters
                  :initialDate="startDate"
                  :finalDate="endDate"
                  @updateInitialDate="
                    startDate = $event;
                    getDRBFiles();
                  "
                  @updateFinalDate="
                    endDate = $event;
                    getDRBFiles();
                  "
                />
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12">
                <v-data-table
                  :headers="headers"
                  :items="files"
                  :loading="loading"
                  :options="pagination"
                  :footer-props="{
                    'items-per-page-options': [5, 10, 25, 50, 100]
                  }"
                  :server-items-length="totalFiles"
                  :items-per-page="pagination.itemsPerPage"
                  @update:options="pagination = $event"
                  ref="files-data-table"
                >
                  <template v-if="files.length" v-slot:body="{ items }">
                    <tbody>
                      <tr
                        v-for="item in items"
                        :key="item.id"
                        ref="table-file-item"
                      >
                        <td>{{ item.id }}</td>
                        <td>
                          {{ item.attributes.created_at | momentUnix("LLL") }}
                        </td>
                        <td>
                          {{ item.attributes.name }}
                        </td>
                        <td>
                          {{ item.attributes.sequence }}
                        </td>
                        <td>
                          {{ item.attributes.num_regs }}
                        </td>
                        <td>
                          {{ item.attributes.sum_values }}
                        </td>
                        <td>
                          <v-btn
                            ref="button-file-item-download"
                            @click="
                              downloadFile(
                                item.attributes.payload,
                                item.attributes.name
                              )
                            "
                            icon
                          >
                            <v-icon>save</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-navigation-drawer
      :value="isGenerateDialogOpen"
      :permanent="isGenerateDialogOpen"
      width="80%"
      temporary
      fixed
      right
      style="transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1);"
      class="bg-gray"
    >
      <Generate
        v-if="isGenerateDialogOpen"
        @close="closeGenerateDialog"
        @meta="openMetaModal"
      />
    </v-navigation-drawer>
    <v-navigation-drawer
      :value="isUploadDialogOpen"
      :permanent="isUploadDialogOpen"
      :width="windowSize.x > 600 ? '40vw' : '100vw'"
      temporary
      fixed
      right
      style="transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1);"
      class="bg-gray"
    >
      <Upload v-if="isUploadDialogOpen" @meta="openMetaModal" />
    </v-navigation-drawer>

    <MetaModal
      v-if="showModal"
      :dialog="showModal"
      :data="dataModal"
      :title="$t('general.metadata_response')"
      @close="closeMetaModal"
    />
  </v-container>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import { mapState, mapActions, mapMutations } from "vuex";
import axios from "axios";
import Upload from "@/components/RecurringDebit/Upload";
import Generate from "@/components/RecurringDebit/Generate";
import DateBar from "@/components/Common/DateBar";
import DateFilters from "@/components/Common/DateFilters";
import MetaModal from "@/components/Common/MetaModal";

export default {
  name: "RecurringDebitDashboard",
  components: { Generate, Upload, DateBar, DateFilters, MetaModal },
  data() {
    return {
      showModal: false,
      dataModal: {},
      windowSize: { x: 0, y: 0 },
      loading: false,
      dateRange: 3,
      startDate: this.$moment()
        .startOf("month")
        .format("YYYY-MM-DD"),
      endDate: this.$moment()
        .endOf("day")
        .format("YYYY-MM-DD"),
      pagination: { itemsPerPage: 25 },
      headers: [
        {
          text: "ID",
          value: "id",
          align: "left",
          sortable: false,
          class: "header_color"
        },
        {
          text: this.$t("recurring_debit.main.table.date"),
          value: "attributes.created_at",
          align: "left",
          sortable: false,
          class: "header_color"
        },
        {
          text: this.$t("recurring_debit.main.table.name"),
          value: "attributes.name",
          align: "left",
          sortable: false,
          class: "header_color"
        },
        {
          text: this.$t("recurring_debit.main.table.sequence"),
          value: "attributes.sequence",
          align: "left",
          sortable: false,
          class: "header_color"
        },
        {
          text: this.$t("recurring_debit.main.table.records"),
          value: "attributes.num_regs",
          align: "left",
          sortable: false,
          class: "header_color"
        },
        {
          text: this.$t("recurring_debit.main.table.amount"),
          value: "attributes.sum_values",
          align: "left",
          sortable: false,
          class: "header_color"
        },
        {
          text: this.$t("general.actions"),
          value: "",
          align: "left",
          sortable: false,
          class: "header_color"
        }
      ],
      selectedFile: null
    };
  },
  computed: mapState({
    isGenerateDialogOpen: ({ recurringDebit: { isGenerateDialogOpen } }) =>
      isGenerateDialogOpen,
    isUploadDialogOpen: ({ recurringDebit: { isUploadDialogOpen } }) =>
      isUploadDialogOpen,
    files: ({ recurringDebit: { files } }) => files,
    totalFiles: ({ recurringDebit: { totalFiles } }) => totalFiles,
    // creationDateOrg: ({ auth: { user } }) => user.organization.created_at,
    isDetailsDialogOpen: ({ recurringDebit: { isDetailsDialogOpen } }) =>
      isDetailsDialogOpen,
    creationDateUser: ({ auth: { user } }) => user.created_at
  }),
  watch: {
    pagination() {
      this.getDRBFiles();
    }
  },
  async created() {
    await this.getBanks();
    await this.getFileFormats();
    await this.getSubscriptionStatuses();
  },
  methods: {
    ...mapMutations({
      changeGenerateDrawer: "recurringDebit/changeGenerateDrawer",
      openUploadDialog: "recurringDebit/openUploadDialog",
      openDetailsDialog: "recurringDebit/openDetailsDialog",
      openGenerateDialog: "recurringDebit/openGenerateDialog",
      closeGenerateDialog: "recurringDebit/closeGenerateDialog",
      closeUpdateDialog: "recurringDebit/closeUpdateDialog"
    }),
    ...mapActions({
      getFiles: "recurringDebit/getFiles",
      getBanks: "recurringDebit/getBanks",
      getFileFormats: "recurringDebit/getFileFormats",
      getSubscriptionStatuses: "recurringDebit/getSubscriptionStatuses"
    }),
    openMetaModal(data) {
      this.showModal = true;
      this.dataModal = data;
    },
    closeMetaModal() {
      this.showModal = false;
      this.dataModal = {};
    },
    openUpload() {
      this.openUploadDialog();
    },
    openGenerate() {
      this.openGenerateDialog();
    },
    async downloadFile(url, filename) {
      const requestOptions = {
        url,
        method: "GET",
        responseType: "blob"
      };

      try {
        const { data } = await axios.request(url, requestOptions);

        const blobFile = new Blob([data]);
        const downloadUrl = window.URL.createObjectURL(blobFile);

        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", `${filename}.txt`);
        document.body.appendChild(link);
        link.click();

        link.remove();
      } catch (error) {
        this.$noty.error(this.$t("downloads.messages.error_download"));
      }
    },
    showDetails(item) {
      this.selectedFile = item.id;
      this.openDetailsDialog();
    },
    getWindowSize() {
      this.windowSize.x = window.innerWidth;
      this.windowSize.y = window.innerHeight;
    },
    async changeDates(initialDate, finalDate, firstFetch) {
      this.startDate = !initialDate ? this.startDate : initialDate;
      this.endDate = !finalDate ? this.endDate : finalDate;
      await this.getDRBFiles();
    },
    async getDRBFiles() {
      this.loading = true;
      const { page, itemsPerPage } = this.pagination;
      await this.getFiles({
        startDate: this.$moment(this.startDate).unix(),
        endDate: this.$moment(this.endDate)
          .endOf("day")
          .unix(),
        page,
        rowsPerPage: itemsPerPage
      });
      this.loading = false;
    },
    handlerInputDetails(arg) {
      if (!arg) {
        this.closeDetailsDialog();
      }
    }
  }
};
</script>

<style>
.custom_card {
  border: 1px solid #d0d3db !important;
}

.header_color {
  color: #2661ff !important;
}
</style>
